import React, { useMemo } from 'react';
import { ResultFormValues } from '../../../../components/ControlLayout/hooks/useResultForm';
import { Controller } from 'react-hook-form';
import { useResultFormContext } from '../../../../components/ControlLayout/ControlLayout';
import QuillEditor from 'react-quill';
import {
  EMPTY_QUILL_VALUE,
  isEmptyQuillValue,
  normalizeForQuill,
} from '../../../../utils/normalizeForQuill';

import { S } from './Textarea.styles';

type Props = {
  label: string;
  placeholder: string;
  name: keyof ResultFormValues;
};

const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'color',
];

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'link'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export const Textarea = React.memo((props: Props) => {
  const { control } = useResultFormContext();

  return (
    <Controller
      name={props.name}
      control={control}
      defaultValue=""
      render={({ field }) => (
        <QuillField
          label={props.label}
          placeholder={props.placeholder}
          value={(field.value as string) || ''}
          onChange={field.onChange}
        />
      )}
    />
  );
});

type QuillFieldProps = {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
};

const QuillField = React.memo((props: QuillFieldProps) => {
  const editorValue = useMemo(
    () => normalizeForQuill(props.value),
    [props.value]
  );

  return (
    <S.EditorContainer>
      <S.Label>{props.label}</S.Label>
      <QuillEditor
        formats={formats}
        modules={modules}
        theme="snow"
        placeholder={props.placeholder}
        value={editorValue || EMPTY_QUILL_VALUE}
        onChange={(content, _delta, source) => {
          // Ignore Quill's own API/silent updates so remounts and value sync
          // cannot overwrite form state with stripped HTML.
          if (source !== 'user') return;
          props.onChange(isEmptyQuillValue(content) ? '' : content);
        }}
      />
    </S.EditorContainer>
  );
});
